import { type JSX, type ReactNode, useMemo } from 'react';

import { cn } from '@change/ds-utils';

import { useTracking } from '@change-corgi/core/react/tracking';

import { CarouselDots } from './components/CarouselDots';
import { NavigationArrows } from './components/NavigationArrows';
import { useCardCarousel } from './hooks/useCardCarousel';

type SharingToolsContext = 'petition_dashboard_grow_petition_v1' | 'petition_dashboard_grow_petition_v2';

type CardCarouselProps = {
	items: Array<{
		key: string;
		component: ReactNode;
	}>;
	fullWidthItems?: boolean;
	trackingContext?: SharingToolsContext;
	initialIndex?: number;
	asLoop?: boolean; // True: Only works fine on variant2
};
export function CardCarousel({
	items,
	fullWidthItems = true,
	trackingContext,
	initialIndex,
	asLoop = true,
}: CardCarouselProps): JSX.Element {
	const loop = items.length > 1 && asLoop;
	const extendedItems = useMemo(() => {
		if (!loop) return items;
		const first = items[0];
		const last = items[items.length - 1];
		return [{ ...last, key: `${last.key}-clone-start` }, ...items, { ...first, key: `${first.key}-clone-end` }];
	}, [items, loop]);

	const {
		scrollContainerRef,
		activeIndex,
		canScrollLeft,
		canScrollRight,
		needsScroll,
		handleScrollLeft,
		handleScrollRight,
		setItemRef,
	} = useCardCarousel({
		itemCount: extendedItems.length,
		initialIndex: (initialIndex ?? 0) + (loop ? 1 : 0),
	});
	const track = useTracking();

	const handleTrackedScrollLeft = () => {
		if (trackingContext) {
			void track('petition_manage_grow_your_petition_arrow_click', {
				context: trackingContext,
			});
		}
		handleScrollLeft();
	};

	const handleTrackedScrollRight = () => {
		if (trackingContext) {
			void track('petition_manage_grow_your_petition_arrow_click', {
				context: trackingContext,
			});
		}
		handleScrollRight();
	};

	return (
		<div className="relative flex w-full flex-col gap-5 md:gap-3">
			<div className="relative">
				{needsScroll && (
					<NavigationArrows
						canScrollLeft={canScrollLeft}
						canScrollRight={canScrollRight}
						onScrollLeft={handleTrackedScrollLeft}
						onScrollRight={handleTrackedScrollRight}
					/>
				)}
				<div
					ref={scrollContainerRef}
					className={cn(
						'flex w-full snap-x snap-mandatory flex-row gap-5 overflow-x-auto [ms-overflow-style:none] [scrollbar-width:none]',
						fullWidthItems ? 'md:gap-12' : 'md:gap-5',
					)}
				>
					{extendedItems.map(({ component, key }, index) => (
						<div
							key={key}
							ref={setItemRef(index)}
							className={cn(
								'flex-shrink-0 snap-center',
								fullWidthItems && 'md:min-w-full',
								fullWidthItems && 'first:ml-4 last:mr-4',
								!fullWidthItems && 'first:ml-4 md:first:ml-0',
							)}
						>
							{component}
						</div>
					))}
				</div>
			</div>
			{needsScroll && fullWidthItems && <CarouselDots totalItems={items.length} activeIndex={activeIndex} />}
		</div>
	);
}
